import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { FormFieldComponent } from '../form-field/form-field.component';
import { filter, takeUntil } from 'rxjs/operators';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { IIcon } from '../../../icon/icon.component';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
  IAutoCompleteOptions,
} from '../../../../directives/auto-complete/auto-complete.interface';
import {
  ITooltip,
  TooltipType,
} from '../../../../services/tooltip-sequence/i2g-tooltip-sequence.data';
import { I2gTooltipSequenceService } from '../../../../services/tooltip-sequence/i2g-tooltip-sequence.service';
import { I2gInputFieldAutocompleteType } from './input-field.interface';

@Component({
  selector: 'i2g-input-field',
  templateUrl: 'input-field.component.html',
  styleUrls: ['input-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldComponent
  extends FormFieldComponent
  implements OnChanges, OnDestroy {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() labelIcon: IIcon;
  @Input() labelTooltip: ITooltip;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() description: string;
  @Input() placeholder: string;
  @Input() fieldControl: AbstractControl;
  @Input() leftIcon: IIcon;
  @Input() rightIcon: IIcon;
  @Input() type = 'text';
  @Input() focusableTrigger$: Observable<void>;
  @Input() maxlength = -1;
  @Input() e2eId: string;
  @Input() autocomplete: I2gInputFieldAutocompleteType;
  @Input() layoutDirection: 'row' | 'column' = 'row';
  @Input() valueAlignment: 'left' | 'center' | 'right' = 'left';
  @Input() errorMessages: IFormFieldErrorMessages;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingScreenName: string;
  @Input() autoCompleteOptions: { autoComplete: IAutoCompleteOptions };

  @Output() inputFocus: EventEmitter<string> = new EventEmitter<string>();
  @Output() inputBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output() inputRightIconClick: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  inputKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output()
  autoCompleteFetchStart: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  autoCompleteFetchEnd: EventEmitter<IAutoCompleteOnFetchEnd> = new EventEmitter<IAutoCompleteOnFetchEnd>();
  @Output()
  autoCompleteSet: EventEmitter<IAutoCompleteOnSet> = new EventEmitter<IAutoCompleteOnSet>();
  @Output() autoCompleteHide: EventEmitter<void> = new EventEmitter<void>();
  @Output() autoCompleteDestroy: EventEmitter<void> = new EventEmitter<void>();

  nativeInput: HTMLInputElement;

  private _fieldSub: Subscription;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _tooltipService: I2gTooltipSequenceService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingService: I2gNgTrackingService
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.fieldControl ||
        changes.trackingObject ||
        changes.trackingScreenName) &&
      this.fieldControl &&
      this.trackingObject &&
      this.trackingScreenName
    ) {
      if (this._fieldSub) {
        this._fieldSub.unsubscribe();
      }

      this._fieldSub = this.fieldControl.statusChanges
        .pipe(
          takeUntil(this.destroyed),
          filter(() => this.fieldControl.invalid)
        )
        .subscribe(() => {
          const errors = this.fieldControl.errors;
          let errorTrackingAction: ITrackingAction;

          if (errors.required) {
            errorTrackingAction = this._trackingActionBuilder.buildErrorInvoked(
              'field_not_populated',
              this.fieldId,
              this.trackingScreenName
            );
          } else if (errors.maxlength) {
            errorTrackingAction = this._trackingActionBuilder.buildErrorInvoked(
              'field_length_exceeded',
              this.fieldId,
              this.trackingScreenName
            );
          }

          if (errorTrackingAction) {
            this._trackingService.trackEvent(
              this.trackingObject,
              errorTrackingAction
            );
          }
        });
    }

    super.ngOnInit();
  }

  showInfoTooltip(e: Event) {
    e.preventDefault();

    this._tooltipService.start({
      isDismissible: true,
      showProgress: false,
      tooltips: [this.labelTooltip],
      tooltipType: TooltipType.default,
    });
  }

  ngOnDestroy() {
    if (this._fieldSub) {
      this._fieldSub.unsubscribe();
    }

    super.ngOnDestroy();
  }
}
